import {
  ANONYMOUS_VIEWER,
  franchiseeDiscountPercentOf,
  viewerPriceCents,
  type Currency,
  type Locale,
  type PriceViewer,
  type Product,
} from "@nh/shared";

/**
 * Filtrar, ordenar y resolver precios. Funciones PURAS, sin React: así se
 * prueban con un array y sin montar la pantalla, que es donde estos bugs
 * (el orden que empata mal, la moneda que no existe) se esconden.
 */

/** Los cuatro órdenes del catálogo. Los mismos que `productListQuerySchema`. */
export const SORT_VALUES = ["recommended", "price-asc", "price-desc", "newest"] as const;
export type SortValue = (typeof SORT_VALUES)[number];

/** Etiqueta en español de cada orden — es también su clave de traducción. */
export const SORT_LABELS: Record<SortValue, string> = {
  recommended: "Recomendado",
  "price-asc": "Precio: de menor a mayor",
  "price-desc": "Precio: de mayor a menor",
  newest: "Más recientes",
};

/**
 * Qué precio es el que se está enseñando. `store` es el propio de una
 * tienda personalizada, que no pasa por `viewerPriceCents`.
 */
export type PriceTier = "public" | "member" | "franchisee" | "store";

export interface ResolvedPrice {
  cents: number;
  currency: Currency;
  /** Precio tachado, cuando hay rebaja. */
  compareAtCents?: number;
  /** `true` cuando el producto no se vende en la moneda que se pidió. */
  isFallbackCurrency: boolean;
  /** Público, de socio, o de franquiciatario (ver `viewerPriceCents`). */
  tier: PriceTier;
  /** El porcentaje que se le restó al de socio, solo en `franchisee`. */
  franchiseeDiscountPercent?: number;
}

/**
 * El precio de un producto en la moneda activa, PARA QUIEN LO MIRA.
 *
 * La regla de quién paga qué vive en `@nh/shared` (`viewerPriceCents`) y
 * es la misma que aplica el cobro: anónimo → público; con sesión → socio;
 * con franquicia → socio menos su descuento. Aquí solo se resuelve la
 * moneda y el tachado.
 *
 * Un producto puede no venderse en las dos monedas (`priceMapSchema` las
 * tiene ambas opcionales). Cuando falta la pedida se cae a la otra y se
 * marca `isFallbackCurrency`, para que la tarjeta pueda decir en qué
 * moneda está mostrando ese precio en vez de mentir con el símbolo.
 *
 * Devuelve `null` si el producto no tiene NINGÚN precio: eso no se pinta
 * como "$0.00" —que se lee como gratis— sino como "precio a consultar".
 */
export function priceFor(
  product: Product,
  currency: Currency,
  viewer: PriceViewer = ANONYMOUS_VIEWER,
): ResolvedPrice | null {
  const other: Currency = currency === "MXN" ? "USD" : "MXN";
  const resolvedCurrency = product.prices[currency] !== undefined ? currency : other;
  const cents = viewerPriceCents(product, resolvedCurrency, viewer);

  if (cents === undefined) return null;

  const tier: PriceTier = !viewer.isSignedIn ? "public" : viewer.isFranchisee ? "franchisee" : "member";

  // El tachado: la rebaja capturada si la hay; si no, el precio público
  // cuando quien mira paga menos que él — es lo que se ahorra por ser
  // socio o franquiciatario, y es lo que producción enseña.
  const publicCents = product.prices[resolvedCurrency];
  const captured = product.compareAtPrices?.[resolvedCurrency];
  const candidate = captured ?? (tier !== "public" ? publicCents : undefined);

  return {
    cents,
    currency: resolvedCurrency,
    // Un tachado que no es mayor que el precio no es una rebaja, es un
    // error de captura. Mostrarlo haría dudar del precio de al lado.
    ...(candidate !== undefined && candidate > cents ? { compareAtCents: candidate } : {}),
    isFallbackCurrency: resolvedCurrency !== currency,
    tier,
    ...(tier === "franchisee" ? { franchiseeDiscountPercent: franchiseeDiscountPercentOf(product) } : {}),
  };
}

/** Las categorías presentes en un catálogo, sin repetir y en orden alfabético. */
export function categoriesOf(products: readonly Product[]): string[] {
  const found = new Set<string>();
  for (const product of products) {
    if (product.category) found.add(product.category);
  }
  return [...found].sort((a, b) => a.localeCompare(b));
}

/** `null` = todas las categorías. */
export function filterByCategory(
  products: readonly Product[],
  category: string | null,
): Product[] {
  if (!category) return [...products];
  return products.filter((product) => product.category === category);
}

function createdAtMillis(product: Product): number {
  const value = Date.parse(product.createdAt);
  return Number.isNaN(value) ? 0 : value;
}

/**
 * Ordena una copia — nunca el array que llega.
 *
 * `Array.prototype.sort` muta, y el array que llega aquí es el de la
 * caché de TanStack Query: ordenarlo en el sitio cambiaría los datos de
 * todos los demás componentes que lean esa misma caché.
 *
 * Un producto SIN precio en `price-asc`/`price-desc` se va al final en los
 * dos sentidos: tratarlo como 0 lo pondría el primero de la lista barata,
 * que es justo donde no debe estar algo cuyo precio se desconoce.
 */
export function sortProducts(
  products: readonly Product[],
  sort: SortValue,
  currency: Currency,
): Product[] {
  const copy = [...products];

  if (sort === "newest") {
    return copy.sort((a, b) => createdAtMillis(b) - createdAtMillis(a));
  }

  if (sort === "recommended") {
    // El orden que decidió quien administra el catálogo (`sortOrder` más
    // bajo primero); a igualdad, lo más reciente arriba.
    return copy.sort(
      (a, b) => a.sortOrder - b.sortOrder || createdAtMillis(b) - createdAtMillis(a),
    );
  }

  const direction = sort === "price-asc" ? 1 : -1;
  return copy.sort((a, b) => {
    const priceA = priceFor(a, currency);
    const priceB = priceFor(b, currency);
    if (!priceA && !priceB) return 0;
    if (!priceA) return 1;
    if (!priceB) return -1;
    return (priceA.cents - priceB.cents) * direction;
  });
}

/**
 * El idioma de i18next (`"es"`, `"en-US"`, `"es-MX"`) al `Locale` del
 * contrato. `contentFor` solo entiende `"es"` y `"en"`, y todo lo que no
 * sea inglés cae al español, que es el idioma obligatorio del catálogo.
 */
export function localeOf(language: string | undefined): Locale {
  return language?.toLowerCase().startsWith("en") ? "en" : "es";
}

/** El locale de formato de números que corresponde a cada idioma. */
export function numberLocaleOf(locale: Locale): string {
  return locale === "en" ? "en-US" : "es-MX";
}
